import React, { useMemo, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";

import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { Button, IconButton, Stack, Typography } from "@mui/material";
import { RoleComponentGuard } from "components";
import { UpgradePlanModal } from "components/modals";
import { Routes } from "constants/routes";
import { useDevice, useModals } from "hooks";
import { useAccountSelector, useAreOnlyExternalAccountsSelect, useDealTitleSelector } from "store";

import { RoleName } from "../../constants";
import { HamburgerMenu } from "../HamburgerMenu";

import "./MobileHeader.styles.scss";

const getPageTitle = (pathname: string, dealTitle?: string): string => {
  if (pathname === Routes.myDeals) {
    return "My Deals";
  }

  if (pathname === Routes.freemium || pathname === Routes.freemiumTools || pathname === Routes.freemiumCommunity) {
    return "Home";
  }

  if (pathname === Routes.dashboard) {
    return "Dashboard";
  }

  if (pathname === Routes.taskManagement) {
    return "Task Management";
  }

  if (pathname === Routes.quickView) {
    return "Smart Sheet";
  }

  if (pathname === Routes.benchmark) {
    return "Benchmark";
  }

  if (pathname === Routes.social || pathname === Routes.socialProfile) {
    return "Social";
  }

  if (pathname === Routes.documents) {
    return "Documents";
  }

  if (
    pathname === Routes.reports ||
    matchPath(pathname, Routes.timeline) ||
    matchPath(pathname, Routes.snapshot) ||
    matchPath(pathname, Routes.classic)
  ) {
    return "Portfolio Analysis";
  }

  if (matchPath(pathname, Routes.monitoring) || matchPath(pathname, Routes.monitoringData)) {
    return "Data Operations";
  }

  if (matchPath(pathname, Routes.dataRooms) || matchPath(pathname, Routes.dataRoom)) {
    return "Data Rooms";
  }

  if (matchPath(pathname, Routes.deal)) {
    return dealTitle || "Deal";
  }

  if (pathname === Routes.usersManagement || pathname === Routes.users || pathname === Routes.teams) {
    return "Entity Management";
  }

  if (pathname === Routes.investorsList) {
    return "Investors";
  }

  if (pathname === Routes.measures) {
    return "Deal Measures";
  }

  if (pathname === Routes.characteristics) {
    return "Deal Characteristics";
  }

  if (pathname === Routes.externalReport) {
    return "External Reports";
  }

  if (pathname === Routes.portfolioConstructionInvestor) {
    return "Portfolio Construction";
  }

  return "Home";
};

export const MobileHeader: React.FC = () => {
  const { pathname } = useLocation();
  const { isMobile } = useDevice();
  const { openUpgradeAccount } = useModals();
  const isShowUpgrade = useAreOnlyExternalAccountsSelect();
  const dealTitle = useDealTitleSelector();
  const [isUpgradePlanModalOpen, setIsUpgradePlanModalOpen] = useState(false);
  const { is_freemium } = useAccountSelector() || {};

  const pageTitle = useMemo(() => getPageTitle(pathname, dealTitle), [pathname, dealTitle]);

  if (!isMobile) {
    return null;
  }

  return (
    <header className="mobile-header">
      <Stack
        direction="row"
        sx={{ alignItems: "center", justifyContent: "space-between", width: "100%", height: "100%" }}
      >
        {/* Left side: Hamburger menu and title */}
        <Stack direction="row" sx={{ alignItems: "center", gap: "12px", flex: 1, minWidth: 0 }}>
          <HamburgerMenu />
          <Typography
            sx={{
              fontSize: "24px",
              fontWeight: 500,
              color: "#071F35",
              lineHeight: "32px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis"
            }}
          >
            {pageTitle}
          </Typography>
        </Stack>

        {/* Right side: Upgrade button and chat icon */}
        <Stack direction="row" sx={{ alignItems: "center", gap: "10px", flexShrink: 0 }}>
          {is_freemium && (
            <Button onClick={() => setIsUpgradePlanModalOpen(true)} size="sm" variant="contained">
              Upgrade
            </Button>
          )}
          {isShowUpgrade && (
            <RoleComponentGuard availableRoles={[RoleName.Guest, RoleName.Viewer]}>
              <Button
                onClick={openUpgradeAccount}
                variant="contained"
                sx={{
                  backgroundColor: "#071F35",
                  color: "#ffffff",
                  fontSize: "16px",
                  fontWeight: 400,
                  lineHeight: "24px",
                  padding: "4px 16px",
                  borderRadius: "32px",
                  textTransform: "none",
                  maxWidth: "240px",
                  "&:hover": {
                    backgroundColor: "#0a3142"
                  }
                }}
              >
                Upgrade
              </Button>
            </RoleComponentGuard>
          )}
          <IconButton
            sx={{
              padding: "6px",
              borderRadius: "32px",
              color: "#071F35",
              minWidth: "auto",
              width: "auto"
            }}
          >
            <ChatBubbleOutlineIcon sx={{ fontSize: "20px", width: "20px", height: "20px" }} />
          </IconButton>
        </Stack>
      </Stack>
      <UpgradePlanModal
        isOpen={isUpgradePlanModalOpen}
        onClose={() => setIsUpgradePlanModalOpen(false)}
        manualUpgrade
      />
    </header>
  );
};
